import ExcelJS from "exceljs";

import type { UtilisationExcelRow } from "~/modules/reports/api/utilisationReport/utilisationReportTypes.ts";

type CellValue = string | number | Date | null;

type CellRef = (key: string, rowIndex: number) => string;

type DataColumn = {
	kind: "data";
	key: string;
	header: string;
	width: number;
	numFmt?: string;
	value: (row: UtilisationExcelRow) => CellValue;
};

type FormulaColumn = {
	kind: "formula";
	key: string;
	header: string;
	width: number;
	numFmt: string;
	formula: (rowIndex: number, ref: CellRef) => string;
};

type ColumnDef = DataColumn | FormulaColumn;

function mapBillable(value: boolean | null): string {
	if (value === true) return "Ja";
	if (value === false) return "Nein";
	return "";
}

function columnIndexToLetter(index: number): string {
	let result = "";
	let n = index;
	while (n >= 0) {
		result = String.fromCharCode((n % 26) + 65) + result;
		n = Math.floor(n / 26) - 1;
	}
	return result;
}

function buildCellRef(columns: ColumnDef[]): CellRef {
	const letterByKey = new Map<string, string>();
	columns.forEach((col, i) => {
		if (letterByKey.has(col.key)) {
			throw new Error(`Duplicate column key: ${col.key}`);
		}
		letterByKey.set(col.key, columnIndexToLetter(i));
	});
	return (key, rowIndex) => {
		const letter = letterByKey.get(key);
		if (!letter) throw new Error(`Unknown column key: ${key}`);
		return `${letter}${rowIndex}`;
	};
}

const COLUMNS: ColumnDef[] = [
	{ kind: "data", key: "employmentType", header: "Intern/Extern", width: 14, value: (r) => r.employmentType },
	{ kind: "data", key: "staffMemberName", header: "Mitarbeiter:in", width: 25, value: (r) => r.staffMemberName },
	{ kind: "data", key: "careerLevel", header: "Karrierestufe", width: 25, value: (r) => r.careerLevel },
	{ kind: "data", key: "fte", header: "FTE", width: 8, numFmt: "0%", value: (r) => r.fte },
	{ kind: "data", key: "month", header: "Monat", width: 12, value: (r) => r.month },
	{ kind: "data", key: "workingDaysInMonth", header: "Arbeitstage", width: 12, value: (r) => r.workingDaysInMonth },
	{ kind: "data", key: "workingDaysFte", header: "Arbeitstage FTE", width: 16, numFmt: "0.00", value: (r) => r.workingDaysFte },
	{ kind: "data", key: "date", header: "Datum", width: 12, numFmt: "DD.MM.YYYY", value: (r) => new Date(r.date) },
	{ kind: "data", key: "type", header: "Typ", width: 16, value: (r) => r.type },
	{ kind: "data", key: "client", header: "Kunde", width: 20, value: (r) => r.client },
	{ kind: "data", key: "project", header: "Projekt", width: 25, value: (r) => r.project },
	{ kind: "data", key: "projectNumber", header: "Projektnummer", width: 16, value: (r) => r.projectNumber },
	{ kind: "data", key: "phase", header: "Phase", width: 25, value: (r) => r.phase },
	{ kind: "data", key: "purchaseOrder", header: "PO", width: 20, value: (r) => r.purchaseOrder },
	{ kind: "data", key: "location", header: "Location", width: 15, value: (r) => r.location },
	{ kind: "data", key: "text", header: "Text", width: 30, value: (r) => r.text },
	{ kind: "data", key: "correctionFactor", header: "Korrekturfaktor", width: 16, numFmt: "0.00", value: (r) => r.correctionFactor },
	{ kind: "data", key: "rawMinutes", header: "Dauer (Min.)", width: 14, numFmt: "#,##0", value: (r) => r.rawMinutes },
	{
		kind: "formula",
		key: "correctedMinutes",
		header: "Dauer korrigiert (Min.)",
		width: 20,
		numFmt: "#,##0",
		formula: (n, ref) => `${ref("correctionFactor", n)}*${ref("rawMinutes", n)}`,
	},
	{
		kind: "data",
		key: "dailyRate",
		header: "Tagessatz (€)",
		width: 16,
		numFmt: "#,##0.00",
		value: (r) => r.rawDailyRateCents != null ? r.rawDailyRateCents / 100 : null,
	},
	{
		kind: "formula",
		key: "correctedDailyRate",
		header: "Tagessatz korrigiert (€)",
		width: 22,
		numFmt: "#,##0.00",
		formula: (n, ref) =>
			`IF(${ref("correctedMinutes", n)}="","",${ref("dailyRate", n)}/${ref("correctionFactor", n)})`,
	},
	{
		kind: "formula",
		key: "revenue",
		header: "Umsatz (€)",
		width: 16,
		numFmt: "#,##0.00",
		formula: (n, ref) =>
			`IF(${ref("rawMinutes", n)}="","",${ref("rawMinutes", n)}/480*${ref("correctedDailyRate", n)})`,
	},
	{ kind: "data", key: "billable", header: "Abrechenbar", width: 14, value: (r) => mapBillable(r.billable) },
];

const cellRef = buildCellRef(COLUMNS);
const lastColumnLetter = columnIndexToLetter(COLUMNS.length - 1);

export async function buildUtilisationWorkbook(
	data: UtilisationExcelRow[],
): Promise<ArrayBuffer> {
	const workbook = new ExcelJS.Workbook();
	workbook.calcProperties.fullCalcOnLoad = true;

	const worksheet = workbook.addWorksheet("Auslastung");

	worksheet.columns = COLUMNS.map((col) => ({ width: col.width }));

	const headerRow = worksheet.addRow(COLUMNS.map((col) => col.header));
	headerRow.font = { bold: true };

	for (const row of data) {
		const values = COLUMNS.map((col) => {
			if (col.kind === "formula") return null;
			return col.value(row);
		});

		worksheet.addRow(values);
	}

	const lastRow = data.length + 1;
	for (let rowIndex = 2; rowIndex <= lastRow; rowIndex++) {
		const wsRow = worksheet.getRow(rowIndex);

		COLUMNS.forEach((col, colIndex) => {
			const cell = wsRow.getCell(colIndex + 1);

			if (col.kind === "formula") {
				cell.value = { formula: col.formula(rowIndex, cellRef) };
			}

			if (col.numFmt) {
				cell.numFmt = col.numFmt;
			}
		});
	}

	worksheet.views = [
		{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2" },
	];
	worksheet.autoFilter = `A1:${lastColumnLetter}${lastRow}`;

	return workbook.xlsx.writeBuffer() as Promise<ArrayBuffer>;
}